import {getDamageDiceRoll} from "../../dice/getDamageDiceRoll";
import {getNeighborHexKeys} from "../../coords/getNeighborHexKeys";
import {getBotCombatD6MinRoll} from "../../dice/getBotCombatD6MinRoll";
import {getBotD6RollBonus} from "../../dice/getBotD6RollBonus";
import {getLowRatingAdvantageDicePenalty} from "../../dice/getLowRatingAdvantageDicePenalty";
import {checkIfNeutralPlayer} from "../../players/checkIfNeutralPlayer";
import {isUnitFaction} from "../../factions/isUnitFaction";
import {FACTIONS} from "../../../constants/factions";

// Кости отряда из записи (переброс, уже идущий бой). diceRoll3/diceRoll4 есть
// только у селестиалов (4 кости, см. getMeleeDiceRolls) — у остальных отрядов
// поля не появляются, поэтому записи старых матчей пересчитываются как прежде
const pickDiceRolls = ({diceRoll1, diceRoll2, diceRoll3, diceRoll4}) => (
  diceRoll3 !== undefined && diceRoll4 !== undefined
    ? {diceRoll1, diceRoll2, diceRoll3, diceRoll4}
    : {diceRoll1, diceRoll2}
);

export const calcMeleeCombatInput = (units, currentPlayerId, pendingMeleeDamage = [], damageToChange = {}, players = null) => {
  const unitToEnemiesMapping = {};
  const combatUnits = [];

  // Кости принадлежат ВЛАДЕЛЬЦУ отряда: подкрутка d6 бота его уровнем бонуса
  // (у «божества» d6 всегда 6, см. getBotD6MinRoll и getBotD6RollBonus)
  // применяется по-отрядно, независимо от того, чей клиент/сервер исполняет
  // расчет боя (players опционален — вызовы без него бросают честные кости
  // всем, как раньше). Отряду бота, зажатому игроком под давлением ботов, пол
  // броска поднимается еще на 1 — достаточно одного такого противника среди
  // соседей (см. getBotCombatD6MinRoll). Подкрутка уровнем бонуса снимается,
  // если среди соседей-противников есть отряд бота: кость урона «всегда 0» у
  // божественного бота — только против живых игроков и нейтральных отрядов
  // (см. checkIfAnyOpponentIsBot).
  // И наоборот, фора новичкам: отряд бота и НЕЙТРАЛЬНЫЙ отряд, зажатый игроком
  // с форой, бросает со штрафом к грани (см. getLowRatingAdvantageDicePenalty).
  // Нейтрального владельца в players нет — его узнаем по самому отряду
  const findPlayer = (playerId) => players.find((player) => player.id === playerId);

  const getEnemyOwners = (enemyUnits) => (
    [...new Set(enemyUnits.map((enemyUnit) => enemyUnit.ownerId))].map(findPlayer)
  );

  const getUnitCombatMinRoll = (unit, enemyUnits) => {
    if (!players) {
      return 1;
    }
    return getBotCombatD6MinRoll(findPlayer(unit.ownerId), getEnemyOwners(enemyUnits));
  };

  const getUnitRollBonus = (unit, enemyUnits) => (
    players ? getBotD6RollBonus(findPlayer(unit.ownerId), getEnemyOwners(enemyUnits)) : 0
  );

  const getUnitFacePenalty = (unit, enemyUnits) => {
    if (!players) {
      return 0;
    }
    return getLowRatingAdvantageDicePenalty(
      findPlayer(unit.ownerId),
      getEnemyOwners(enemyUnits),
      {isNeutralRoller: checkIfNeutralPlayer(unit.ownerId)}
    );
  };

  units.forEach(unit => {
    const neighborKeys = getNeighborHexKeys(unit.hexKey);
    units.forEach(otherUnit => {
      if (neighborKeys.includes(otherUnit.hexKey)) {
        if (unit.ownerId !== otherUnit.ownerId
          && (unit.ownerId === currentPlayerId || otherUnit.ownerId === currentPlayerId)
        ) {
          if (!unitToEnemiesMapping[unit.id]) {
            unitToEnemiesMapping[unit.id] = [];
          }
          unitToEnemiesMapping[unit.id].push(otherUnit);
        }
      }
    });
    if (unitToEnemiesMapping[unit.id]) {
      let unitForCombat;
      const damageToChangeForUnit = damageToChange[unit.id];
      if (damageToChangeForUnit) {
        unitForCombat = {
          ...unit,
          ...pickDiceRolls(damageToChangeForUnit),
        }
      } else {
        const pendingDamageForUnit = pendingMeleeDamage.find(action => {
          return unit.id === action.unitId
        })
        if (pendingDamageForUnit) {
          unitForCombat = {
            ...unit,
            ...pickDiceRolls(pendingDamageForUnit),
          }
        } else {
          const ownerMinRoll = getUnitCombatMinRoll(unit, unitToEnemiesMapping[unit.id]);
          const ownerRollBonus = getUnitRollBonus(unit, unitToEnemiesMapping[unit.id]);
          // Штраф идет в сам бросок и «запекается» в грань: уже выпавшие кости
          // (ветки выше) при пересчетах боя берутся как есть и повторно не
          // штрафуются
          const ownerFacePenalty = getUnitFacePenalty(unit, unitToEnemiesMapping[unit.id]);
          const rollDamageDice = () => getDamageDiceRoll(ownerMinRoll, ownerRollBonus, ownerFacePenalty);
          unitForCombat = {
            ...unit,
            diceRoll1: rollDamageDice(),
            diceRoll2: rollDamageDice(),
          }
          // Селестиалы (по фракции ОТРЯДА — и соблазненные) бросают
          // CELECTIALS_MELEE_DAMAGE_DICE_COUNT костей, в урон идут лучшие
          // (см. getMeleeCountedDiceRolls)
          if (isUnitFaction(unit, FACTIONS.CELECTIALS)) {
            unitForCombat.diceRoll3 = rollDamageDice();
            unitForCombat.diceRoll4 = rollDamageDice();
          }
        }
      }
      combatUnits.push(unitForCombat);
    }
  });

  return {combatUnits, unitToEnemiesMapping};
}